/**
 * Studio state: a single reducer owns everything the canvas needs to render a
 * frame, so the preview, the timeline and the exporter can never disagree.
 */
import {
  KEYFRAME_MIN_GAP,
  clampKeyframe,
  makeKeyframe,
  patchKeyframe,
  sortKeyframes,
  type ZoomKeyframe,
} from '../lib/keyframes'
import type { AspectRatioId, ResolutionId } from '../lib/framing'
import { DEFAULT_WATERMARK, DEFAULT_WINDOW_STYLE, type WatermarkOptions, type WindowStyle } from '../engine/compositor'
import { CUSTOM_WALLPAPER_ID, DEFAULT_WALLPAPER_ID, WALLPAPERS } from '../lib/wallpapers'
import type { TelemetrySample } from '../lib/telemetry'
import { IDLE_EXPORT_STATE, type ExportState, type Notice, type VideoMeta } from './types'

export interface StudioState {
  video: VideoMeta | null
  /** Object URL for the loaded source recording. */
  videoSrc: string | null
  keyframes: ZoomKeyframe[]
  selectedKeyframeId: string | null
  aspect: AspectRatioId
  resolution: ResolutionId
  wallpaperId: string
  customColor: string
  style: WindowStyle
  watermark: WatermarkOptions
  /** Preferred export container/codec id (resolved against browser support). */
  formatId: string | null
  cursorSamples: TelemetrySample[]
  showCursor: boolean
  /** Auto-generate keyframes from telemetry clicks after a capture. */
  autoZoomFromClicks: boolean
  currentTime: number
  duration: number
  isPlaying: boolean
  exportState: ExportState
  notice: Notice | null
  /** Free-tier trim switch for long recordings. */
  exportLimitToFree: boolean
}

export const INITIAL_STUDIO_STATE: StudioState = {
  video: null,
  videoSrc: null,
  keyframes: [],
  selectedKeyframeId: null,
  aspect: '16:9',
  resolution: '720p',
  wallpaperId: DEFAULT_WALLPAPER_ID,
  customColor: '#0f172a',
  style: { ...DEFAULT_WINDOW_STYLE },
  watermark: { ...DEFAULT_WATERMARK },
  formatId: null,
  cursorSamples: [],
  showCursor: true,
  autoZoomFromClicks: true,
  currentTime: 0,
  duration: 0,
  isPlaying: false,
  exportState: { ...IDLE_EXPORT_STATE },
  notice: null,
  exportLimitToFree: false,
}

export type StudioAction =
  | {
      type: 'video/loaded'
      payload: {
        src: string
        meta: VideoMeta
        keyframes?: ZoomKeyframe[]
        cursorSamples?: TelemetrySample[]
      }
    }
  | { type: 'video/cleared' }
  | { type: 'keyframe/add'; payload: { keyframe: ZoomKeyframe } }
  | { type: 'keyframe/addMany'; payload: { keyframes: ZoomKeyframe[] } }
  | { type: 'keyframe/update'; payload: { id: string; patch: Partial<ZoomKeyframe> } }
  | { type: 'keyframe/remove'; payload: { id: string } }
  | { type: 'keyframe/select'; payload: { id: string | null } }
  | { type: 'settings/aspect'; payload: AspectRatioId }
  | { type: 'settings/resolution'; payload: ResolutionId }
  | { type: 'settings/wallpaper'; payload: string }
  | { type: 'settings/customColor'; payload: string }
  | { type: 'settings/style'; payload: Partial<WindowStyle> }
  | { type: 'settings/watermark'; payload: Partial<WatermarkOptions> }
  | { type: 'settings/format'; payload: string }
  | { type: 'settings/toggleAutoZoom' }
  | { type: 'playback/time'; payload: number }
  | { type: 'playback/playing'; payload: boolean }
  | { type: 'cursor/samples'; payload: TelemetrySample[] }
  | { type: 'cursor/toggle' }
  | { type: 'export/state'; payload: Partial<ExportState> }
  | { type: 'export/limitToFree'; payload: boolean }
  | { type: 'notice'; payload: Notice | null }
  | { type: 'project/reset' }

const MAX_KEYFRAMES = 400

/** Add a keyframe, replacing one that starts at (nearly) the same moment. */
function addKeyframe(keyframes: readonly ZoomKeyframe[], incoming: ZoomKeyframe, maxTime: number): ZoomKeyframe[] {
  const next = clampKeyframe(incoming, maxTime)
  const kept = keyframes.filter(
    (keyframe) => Math.abs(keyframe.t - next.t) >= KEYFRAME_MIN_GAP && keyframe.id !== next.id,
  )
  kept.push(next)
  return sortKeyframes(kept).slice(-MAX_KEYFRAMES)
}

export function studioReducer(state: StudioState, action: StudioAction): StudioState {
  switch (action.type) {
    case 'video/loaded': {
      const { meta, src, keyframes, cursorSamples } = action.payload
      return {
        ...state,
        video: meta,
        videoSrc: src,
        duration: meta.duration,
        keyframes: sortKeyframes(keyframes ?? []),
        selectedKeyframeId: null,
        cursorSamples: cursorSamples ?? [],
        currentTime: 0,
        isPlaying: false,
        exportState: { ...IDLE_EXPORT_STATE },
        notice: null,
      }
    }
    case 'video/cleared':
      return {
        ...state,
        video: null,
        videoSrc: null,
        duration: 0,
        keyframes: [],
        selectedKeyframeId: null,
        cursorSamples: [],
        currentTime: 0,
        isPlaying: false,
        exportState: { ...IDLE_EXPORT_STATE },
      }
    case 'keyframe/add':
      return {
        ...state,
        keyframes: addKeyframe(state.keyframes, action.payload.keyframe, state.duration),
        selectedKeyframeId: action.payload.keyframe.id,
      }
    case 'keyframe/addMany':
      return {
        ...state,
        keyframes: sortKeyframes([...state.keyframes, ...action.payload.keyframes]).slice(-MAX_KEYFRAMES),
      }
    case 'keyframe/update':
      return {
        ...state,
        keyframes: sortKeyframes(
          state.keyframes.map((keyframe) =>
            keyframe.id === action.payload.id ? patchKeyframe(keyframe, action.payload.patch, state.duration) : keyframe,
          ),
        ),
      }
    case 'keyframe/remove':
      return {
        ...state,
        keyframes: state.keyframes.filter((keyframe) => keyframe.id !== action.payload.id),
        selectedKeyframeId: state.selectedKeyframeId === action.payload.id ? null : state.selectedKeyframeId,
      }
    case 'keyframe/select':
      return { ...state, selectedKeyframeId: action.payload.id }
    case 'settings/aspect':
      return { ...state, aspect: action.payload }
    case 'settings/resolution':
      return { ...state, resolution: action.payload }
    case 'settings/wallpaper':
      return { ...state, wallpaperId: action.payload }
    case 'settings/customColor':
      return { ...state, customColor: action.payload }
    case 'settings/style':
      return { ...state, style: { ...state.style, ...action.payload } }
    case 'settings/watermark':
      return { ...state, watermark: { ...state.watermark, ...action.payload } }
    case 'settings/toggleAutoZoom':
      return { ...state, autoZoomFromClicks: !state.autoZoomFromClicks }
    case 'playback/time':
      return { ...state, currentTime: Math.max(0, action.payload) }
    case 'playback/playing':
      return state.isPlaying === action.payload ? state : { ...state, isPlaying: action.payload }
    case 'cursor/samples':
      return { ...state, cursorSamples: action.payload }
    case 'cursor/toggle':
      return { ...state, showCursor: !state.showCursor }
    case 'export/state':
      return { ...state, exportState: { ...state.exportState, ...action.payload } }
    case 'export/limitToFree':
      return { ...state, exportLimitToFree: action.payload }
    case 'notice':
      return { ...state, notice: action.payload }
    case 'project/reset':
      return {
        ...INITIAL_STUDIO_STATE,
        aspect: state.aspect,
        resolution: state.resolution,
        wallpaperId: state.wallpaperId,
        customColor: state.customColor,
        style: state.style,
        watermark: state.watermark,
        showCursor: state.showCursor,
        autoZoomFromClicks: state.autoZoomFromClicks,
      }
    default:
      return state
  }
}

/** The subset of state that survives a reload. */
export interface StudioPrefs {
  aspect: AspectRatioId
  resolution: ResolutionId
  wallpaperId: string
  customColor: string
  style: WindowStyle
  watermark: WatermarkOptions
  showCursor: boolean
  autoZoomFromClicks: boolean
}

export const PREFS_STORAGE_KEY = 'demopolish.prefs.v1'

export function prefsFromState(state: StudioState): StudioPrefs {
  return {
    aspect: state.aspect,
    resolution: state.resolution,
    wallpaperId: state.wallpaperId,
    customColor: state.customColor,
    style: { ...state.style },
    watermark: { ...state.watermark },
    showCursor: state.showCursor,
    autoZoomFromClicks: state.autoZoomFromClicks,
  }
}

const WALLPAPER_IDS = new Set<string>(WALLPAPERS.map((wallpaper) => wallpaper.id))

/** Merge persisted prefs into a state object, ignoring anything malformed. */
export function applyPrefs(state: StudioState, prefs: Partial<StudioPrefs> | null | undefined): StudioState {
  if (!prefs || typeof prefs !== 'object') return state
  const next: StudioState = { ...state }
  if (prefs.aspect === '16:9' || prefs.aspect === '1:1' || prefs.aspect === '9:16') next.aspect = prefs.aspect
  if (prefs.resolution === '720p' || prefs.resolution === '1080p' || prefs.resolution === '4k') {
    next.resolution = prefs.resolution
  }
  if (typeof prefs.wallpaperId === 'string' && WALLPAPER_IDS.has(prefs.wallpaperId)) next.wallpaperId = prefs.wallpaperId
  if (typeof prefs.customColor === 'string') next.customColor = prefs.customColor
  if (prefs.style && typeof prefs.style === 'object') {
    next.style = {
      padding: clampNumber(prefs.style.padding, next.style.padding, 0, 120),
      cornerRadius: clampNumber(prefs.style.cornerRadius, next.style.cornerRadius, 0, 32),
      shadow: clampNumber(prefs.style.shadow, next.style.shadow, 0, 100),
      showChrome: prefs.style.showChrome !== false,
    }
  }
  if (prefs.watermark && typeof prefs.watermark === 'object') {
    next.watermark = {
      enabled: prefs.watermark.enabled === true,
      text: typeof prefs.watermark.text === 'string' ? prefs.watermark.text : next.watermark.text,
      opacity: clampNumber(prefs.watermark.opacity, next.watermark.opacity, 0, 1),
      position: next.watermark.position,
    }
  }
  if (typeof prefs.showCursor === 'boolean') next.showCursor = prefs.showCursor
  if (typeof prefs.autoZoomFromClicks === 'boolean') next.autoZoomFromClicks = prefs.autoZoomFromClicks
  return next
}

function clampNumber(value: unknown, fallback: number, min: number, max: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback
  return Math.min(max, Math.max(min, value))
}
